import { FC, HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Flex, Form, Input, notification, Select, Typography } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
	ArbitrationImageUpload,
	ArbitrationUrlForm,
	CreateFormButtons,
	EPurchaseTypes,
	ISource,
	prepareCreateArbitrationData,
	useSourceSlice,
	useUpdateSource,
} from 'entities/Source';
import { EOnboardingStates, EResultTypes, useOnboardingSlice } from 'features/Onboarding';
import { urlValidator } from 'shared/consts';
import { useCommonSlice, useTrans } from 'shared/hooks';
import { EMainRoutes, EYmEvents, EYmEventsO1, EYmEventsO2, IBadRequestError } from 'shared/typings';
import { sendYmEvent } from 'shared/utils';

interface ICreateArbitrationSourceFormProps extends HTMLAttributes<HTMLDivElement> {
	isOnboarding?: boolean;
	source?: ISource;
}

export const CreateArbitrationSourceForm: FC<ICreateArbitrationSourceFormProps> = ({
	className,
	isOnboarding,
	source,
	...props
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { purchaseTypes } = useTrans();

	const [skipVerification, setSkipVerification] = useState(false);

	const { countryOptions } = useCommonSlice();

	const { setModalState, setResultType, setCreatedSource } = useOnboardingSlice();
	const { type, arbitrationValues, setArbitrationValues, clearValues } = useSourceSlice();

	const { addSource, updateSource, addVerification, data, isLoading, isSuccess, isVerificationSuccess, error } =
		useUpdateSource();

	const validationSchema = Yup.object({
		name: Yup.string()
			.required(t('Это поле обязательно для заполнения'))
			.max(64, t('Количество символов не должно превышать {{characters}}', { characters: 64 })),
		url1: Yup.string()
			.required(t('Это поле обязательно для заполнения'))
			.matches(urlValidator, t('Введите корректный URL'))
			.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 })),
		url2: Yup.string()
			.matches(urlValidator, t('Введите корректный URL'))
			.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 })),
		url3: Yup.string()
			.matches(urlValidator, t('Введите корректный URL'))
			.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 })),
		url4: Yup.string()
			.matches(urlValidator, t('Введите корректный URL'))
			.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 })),
		url5: Yup.string()
			.matches(urlValidator, t('Введите корректный URL'))
			.max(4096, t('Количество символов не должно превышать {{characters}}', { characters: 4096 })),
		purchase_type: Yup.string().required(t('Это поле обязательно для заполнения')),
		countries: Yup.array().min(1, t('Это поле обязательно для заполнения')),
	});

	useEffect(() => {
		if (!isSuccess) return;

		sendYmEvent({
			event: EYmEvents.SEND_ADD_ADSPACE_SUCCESS,
			o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE],
			o2: EYmEventsO2.ADD_ADSPACE,
			o4: type,
			wi: data?.uuid,
		});

		if (isOnboarding) {
			setCreatedSource(data || null);

			if (skipVerification) {
				clearValues();
				setModalState(EOnboardingStates.FINAL);
				setResultType(EResultTypes.NEED_VERIFICATION);
			} else {
				addVerification({ body: { source_pk: data?.uuid || '' } });
			}
		} else {
			if (skipVerification) {
				clearValues();
				navigate(`/${EMainRoutes.SOURCES}/`);
				notification.open({
					message: t('Требуется подтвердить владение площадкой'),
					description: t(
						'{{sourceName}} создана успешно, но требуется подтвердить ее владение. Без подтверждения ее владения и прохождения модерации, вы не сможете подавать заявки на подключение к офферам.',
						{ sourceName: data?.name },
					),
					type: 'warning',
					placement: 'topRight',
					showProgress: true,
					pauseOnHover: true,
				});
			} else {
				addVerification({ body: { source_pk: data?.uuid || '' } });
			}
		}
	}, [isSuccess]); // eslint-disable-line

	useEffect(() => {
		if (!isVerificationSuccess) return;

		clearValues();

		if (isOnboarding) {
			setModalState(EOnboardingStates.FINAL);
			setResultType(EResultTypes.SUCCESS);
		} else {
			navigate(`/${EMainRoutes.SOURCES}/`);
			notification.open({
				message: t('Площадка создана успешно'),
				description: t(
					'После подтверждения владения {{sourceName}}, последняя автоматически попадет на модерацию и после ее прохождения можно подавать заявки на подключение к офферам.',
					{ sourceName: data?.name },
				),
				type: 'success',
				placement: 'topRight',
				showProgress: true,
				pauseOnHover: true,
			});
		}
	}, [isVerificationSuccess]); // eslint-disable-line

	return (
		<Formik
			initialValues={arbitrationValues}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				source?.uuid
					? updateSource({ ...prepareCreateArbitrationData(values, type, arbitrationValues), uuid: source.uuid })
					: addSource(prepareCreateArbitrationData(values, type, arbitrationValues));
			}}>
			{({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Flex {...props} vertical gap={24} className={className}>
							<Flex vertical gap={20}>
								<Form.Item
									layout="vertical"
									label={t('Имя арбитражной команды')}
									required
									validateStatus={errors.name && touched.name ? 'error' : ''}
									help={errors.name && touched.name && errors.name}>
									<Input
										name="name"
										placeholder="Укажите название"
										onChange={(e) => {
											setArbitrationValues({ ...arbitrationValues, name: e.target.value });
											handleChange(e);
										}}
										onBlur={handleBlur}
										value={values.name}
										disabled={isLoading}
									/>
									<Typography.Text type="secondary" style={{ marginTop: 2 }}>
										{t('Будет использовано в качестве названия площадки')}
									</Typography.Text>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={t('Что вы покупаете?')}
									required
									validateStatus={errors.purchase_type && touched.purchase_type ? 'error' : ''}
									help={errors.purchase_type && touched.purchase_type && errors.purchase_type}>
									<Select
										options={Object.keys(purchaseTypes).map((key) => ({
											value: key,
											label: purchaseTypes[key as EPurchaseTypes],
										}))}
										onChange={(e) => {
											setArbitrationValues({ ...arbitrationValues, purchase_type: e });
											setFieldValue('purchase_type', e);
										}}
										onBlur={handleBlur}
										value={values.purchase_type}
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите одно')}
										allowClear
										disabled={isLoading}
									/>
								</Form.Item>

								<ArbitrationUrlForm
									values={values}
									handleChange={handleChange}
									handleBlur={handleBlur}
									errors={errors}
									touched={touched}
									apiErrors={(error as IBadRequestError)?.data?.arbitrage_data?.urls}
								/>

								<Form.Item
									layout="vertical"
									label={`${t('Страна аудитории')} (${t('Макс.')} 5)`}
									required
									validateStatus={errors.countries && touched.countries ? 'error' : ''}
									help={errors.countries && touched.countries && errors.countries}>
									<Select
										options={countryOptions}
										onChange={(e) => {
											setArbitrationValues({ ...arbitrationValues, countries: e });
											setFieldValue('countries', e);
										}}
										onBlur={handleBlur}
										value={values.countries}
										mode="multiple"
										maxCount={5}
										maxTagCount="responsive"
										showSearch
										optionFilterProp="label"
										placeholder={t('Выберите локали')}
										allowClear
										disabled={isLoading}
									/>
									<Typography.Text type="secondary" style={{ marginTop: 2 }}>
										{t('Помогите улучшить инструмент для поиска брендов')}
									</Typography.Text>
								</Form.Item>

								<Form.Item
									layout="vertical"
									label={t('Описание')}
									validateStatus={errors.description && touched.description ? 'error' : ''}
									help={errors.description && touched.description && errors.description}>
									<Input.TextArea
										name="description"
										onChange={(e) => {
											setArbitrationValues({ ...arbitrationValues, description: e.target.value });
											handleChange(e);
										}}
										onBlur={handleBlur}
										value={values.description}
										disabled={isLoading}
										style={{ height: 100 }}
									/>
									<Typography.Text type="secondary" style={{ marginTop: 2 }}>
										{t(
											'Необходимо предоставить информацию, которая поможет модератору принять решение о доступе к системе. Добавьте скриншоты из кабинета Рекламной сети и конверсии из партнерских сетей.',
										)}
									</Typography.Text>
								</Form.Item>

								<ArbitrationImageUpload screenshots={arbitrationValues.screenshots} />
							</Flex>

							<CreateFormButtons
								isOnboarding={isOnboarding}
								isLoading={isLoading}
								errors={errors}
								touched={touched}
								setSkipVerification={setSkipVerification}
							/>
						</Flex>
					</form>
				);
			}}
		</Formik>
	);
};
